import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, ListChecks, ShoppingBag, Download, Search,
  CheckCircle2, XCircle, Undo2, Clock, MapPin, Globe, Landmark, CreditCard, Plus, X,
  PenSquare, UserCog, Mail, Send, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button.jsx';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import OrnateDivider from '../components/OrnateDivider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Helpers ---------- */
const fmtINR = (inr) => `₹${(inr || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const METHOD_META = {
  razorpay: { label: 'Razorpay', icon: CreditCard, color: 'text-emerald-300' },
  upi: { label: 'UPI', icon: MapPin, color: 'text-sky-300' },
  bank: { label: 'Bank', icon: Landmark, color: 'text-amber-300' },
  other: { label: 'Other', icon: Globe, color: 'text-fuchsia-300' },
};
const STATUS_META = {
  paid: { label: 'Paid', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' },
  pending_review: {
    label: 'Pending',
    color: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
  },
  rejected: { label: 'Rejected', color: 'bg-rose-500/15 text-rose-300 border-rose-400/30' },
  refunded: { label: 'Refunded', color: 'bg-slate-500/20 text-slate-300 border-slate-400/30' },
};

const toCsv = (rows) => {
  if (!rows.length) return '';
  const cols = [
    'id', 'created_at', 'user_email', 'user_name', 'method', 'status',
    'amount_inr', 'level_number', 'txn_ref', 'order_id', 'screenshot_url', 'notes',
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [cols.join(',')];
  for (const r of rows) lines.push(cols.map((c) => esc(r[c])).join(','));
  return lines.join('\n');
};

/* ---------- Stat card ---------- */
const StatCard = ({ label, value, testid }) => (
  <div className="card-mystical rounded-2xl p-5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.35em] text-primary/60 mb-2">
      {label}
    </div>
    <div className="text-3xl font-heading golden-text">{value}</div>
  </div>
);

/* ---------- Page ---------- */
const MembershipBadge = ({ m }) => {
  if (!m || m.status === 'none' || !m.status) {
    return <span className="text-xs text-primary/50">—</span>;
  }
  const colors = {
    active: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    cancelling: 'bg-amber-500/10 border-amber-500/40 text-amber-200',
    expired: 'bg-rose-500/10 border-rose-500/40 text-rose-300',
  };
  const cls = colors[m.status] || 'bg-primary/10 border-primary/30 text-primary/70';
  const planLabel = m.plan === 'yearly' ? 'Yearly' : m.plan === 'monthly' ? 'Monthly' : m.plan === 'granted' ? 'Granted' : m.plan || '';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${cls}`}>
      {m.status}
      {planLabel && <span className="opacity-70">· {planLabel}</span>}
      {m.source === 'granted' && <span className="opacity-60">(free)</span>}
    </span>
  );
};

const MembershipAdminActions = ({ user, onDone }) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const grant = async (months) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/membership/grant`, { user_id: user.id, months }, auth());
      setOpen(false);
      onDone?.();
    } catch (e) { /* ignore */ }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/membership/revoke`, { user_id: user.id, months: 1 }, auth());
      setOpen(false);
      onDone?.();
    } catch (e) { /* ignore */ }
    finally { setBusy(false); }
  };
  const m = user.membership || {};
  const canRevoke = m.source === 'granted' && m.status === 'active';
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-2 py-1 text-[11px] font-medium uppercase tracking-[0.2em] border border-primary/30 rounded-lg text-primary/90 hover:bg-primary/10 transition"
        data-testid={`ic-admin-actions-${user.id}`}
      >
        Membership
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 z-20 w-44 rounded-lg border border-primary/30 bg-background/95 backdrop-blur shadow-xl p-2"
          data-testid={`ic-admin-menu-${user.id}`}
        >
          <div className="text-[10px] uppercase tracking-[0.25em] text-primary/55 px-2 py-1">Grant free</div>
          {[1, 3, 12].map((m2) => (
            <button
              key={m2}
              type="button"
              disabled={busy}
              onClick={() => grant(m2)}
              className="w-full text-left px-2 py-1.5 text-[12px] text-primary/85 hover:bg-primary/10 rounded"
              data-testid={`ic-grant-${user.id}-${m2}`}
            >
              {m2} month{m2 > 1 ? 's' : ''}
            </button>
          ))}
          {canRevoke && (
            <>
              <div className="h-px bg-primary/15 my-1" />
              <button
                type="button"
                disabled={busy}
                onClick={revoke}
                className="w-full text-left px-2 py-1.5 text-[12px] text-rose-300 hover:bg-rose-500/10 rounded"
                data-testid={`ic-revoke-${user.id}`}
              >
                Revoke granted
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};


const Admin = () => {
  const { user, loading: authLoading, token } = useAuth();
  const { editMode, setEditMode } = useContent();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [rzpStatus, setRzpStatus] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ method: 'all', status: 'all', q: '' });
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [manualForm, setManualForm] = useState({
    user_email: '',
    user_name: '',
    user_phone: '',
    user_country: '',
    method: 'other',
    txn_ref: '',
    amount_inr: 3000,
    notes: '',
  });
  const [adding, setAdding] = useState(false);
  const [accessEditUser, setAccessEditUser] = useState(null); // user row or null
  const [accessLevels, setAccessLevels] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);

  // Blog writers tab
  const [writers, setWriters] = useState({ users: [], total_writers: 0, max_writers: 10 });
  const [writerSearch, setWriterSearch] = useState('');
  const [togglingWriterId, setTogglingWriterId] = useState(null);

  /* ---- Access guard ---- */
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }
    if (!user.is_admin) {
      toast.error('Admin access required');
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const authedGet = useCallback(
    (path, params) =>
      axios.get(`${API}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      }),
    [token]
  );

  const refresh = useCallback(async () => {
    if (!token || !user?.is_admin) return;
    setLoading(true);
    try {
      const [s, e, o, u] = await Promise.all([
        authedGet('/admin/stats'),
        authedGet('/admin/enrollments', {
          method: filters.method,
          status: filters.status,
          q: filters.q || undefined,
        }),
        authedGet('/admin/orders'),
        authedGet('/admin/users'),
      ]);
      setStats(s.data);
      setEnrollments(e.data || []);
      setOrders(o.data || []);
      setUsers(u.data || []);
      // Razorpay mode badge — fetched separately so a probe failure never
      // blocks the rest of the admin dashboard.
      try {
        const rz = await authedGet('/admin/razorpay-status');
        setRzpStatus(rz.data);
      } catch (_rzErr) {
        setRzpStatus(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [authedGet, filters, token, user]);

  useEffect(() => { refresh(); }, [refresh]);

  const fetchWriters = useCallback(async () => {
    if (!token || !user?.is_admin) return;
    try {
      const r = await authedGet('/admin/writers');
      setWriters(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load writers');
    }
  }, [authedGet, token, user]);

  useEffect(() => {
    if (tab === 'writers') fetchWriters();
  }, [tab, fetchWriters]);

  const toggleWriter = async (u) => {
    setTogglingWriterId(u.id);
    try {
      const res = await axios.post(
        `${API}/admin/writers/${u.id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.role === 'writer' ? `${u.name} is now a Writer` : `${u.name} reverted to Reader`);
      fetchWriters();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to toggle role');
    } finally {
      setTogglingWriterId(null);
    }
  };

  const filteredWriterRows = writers.users.filter((u) => {
    const q = writerSearch.toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });
  const atWriterCap = writers.total_writers >= writers.max_writers;

  const updateEnrollment = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/enrollments/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked ${STATUS_META[status]?.label || status}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const downloadCsv = () => {
    const csv = toCsv(enrollments);
    if (!csv) {
      toast.message('Nothing to export');
      return;
    }
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enrollments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitManualEnrollment = async (e) => {
    e.preventDefault();
    if (!manualForm.user_email.trim() || !manualForm.user_name.trim()) {
      toast.error('Email and name are required');
      return;
    }
    setAdding(true);
    try {
      const res = await axios.post(
        `${API}/admin/enrollments/manual`,
        {
          ...manualForm,
          amount_inr: Number(manualForm.amount_inr) || 3000,
          level_number: 1,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        res.data.linked_to_user
          ? 'Enrollment recorded & linked to existing user'
          : 'Enrollment recorded · will auto-link when the user registers'
      );
      setShowAddModal(false);
      setManualForm({
        user_email: '',
        user_name: '',
        user_phone: '',
        user_country: '',
        method: 'other',
        txn_ref: '',
        amount_inr: 3000,
        notes: '',
      });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setAdding(false);
    }
  };

  const openAccessEditor = (u) => {
    setAccessEditUser(u);
    setAccessLevels(Array.isArray(u.enrolled_levels) ? [...u.enrolled_levels] : []);
  };

  const toggleAccessLevel = (n) => {
    setAccessLevels((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort()
    );
  };

  const saveUserAccess = async () => {
    if (!accessEditUser) return;
    setSavingAccess(true);
    try {
      await axios.patch(
        `${API}/admin/users/${accessEditUser.id}/access`,
        { enrolled_levels: accessLevels },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Access updated for ${accessEditUser.email}`);
      setAccessEditUser(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update access');
    } finally {
      setSavingAccess(false);
    }
  };

  const [resettingId, setResettingId] = useState(null);
  const handleResetPassword = async (u) => {
    const ok = window.confirm(
      `Send a password-reset email to ${u.email}?\n\nThey will receive a one-time link (valid 24h) to choose a new password. Their current password keeps working until they use the link.`
    );
    if (!ok) return;
    setResettingId(u.id);
    try {
      const res = await axios.post(
        `${API}/admin/users/${u.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.email_sent) {
        toast.success(`Reset link emailed to ${res.data.email}`);
      } else {
        toast.success(`Reset token created for ${res.data.email} (email skipped — RESEND_API_KEY may be unset on this env)`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to issue reset');
    } finally {
      setResettingId(null);
    }
  };

  const methodCounts = useMemo(() => {
    const out = { razorpay: 0, upi: 0, bank: 0, other: 0 };
    for (const r of enrollments) {
      if (out[r.method] !== undefined) out[r.method] += 1;
    }
    return out;
  }, [enrollments]);

  if (authLoading || !user || !user.is_admin) {
    return (
      <div className="min-h-screen mystical-bg flex items-center justify-center">
        <div className="text-primary/70">Loading…</div>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'enrollments', label: 'Enrollments', icon: ListChecks },
    { id: 'orders', label: 'Razorpay Orders', icon: ShoppingBag },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'writers', label: 'Blog Writers', icon: PenSquare },
    { id: 'emails', label: 'Emails', icon: Mail },
  ];

  return (
    <div className="min-h-screen mystical-bg pt-24 pb-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="text-center mb-8">
            <OrnateDivider label="Admin" variant="diamond" className="mb-4 max-w-md mx-auto" />
            <h1
              className="text-3xl md:text-4xl font-heading font-bold golden-text text-glow tracking-wider"
              data-testid="admin-title"
            >
              Siddha Samaj · Admin
            </h1>
            <p className="text-xs text-primary/60 mt-2 uppercase tracking-[0.3em]">
              Signed in as {user.email}
            </p>
          </div>

          {/* Site-wide Edit Mode toggle. When OFF, the admin sees the site
              exactly like a regular visitor — no pencil icons. When ON, the
              <EditableText> pencil appears next to every editable string. */}
          <div
            className="card-mystical rounded-xl px-5 py-4 mb-6 flex items-center justify-between gap-4 flex-wrap"
            data-testid="admin-edit-mode-card"
          >
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.3em] text-primary/60 mb-1">
                Inline content editing
              </div>
              <div className="text-sm text-foreground/85">
                {editMode
                  ? 'Edit pencils are visible across the site. Click any pencil to edit text in place.'
                  : 'Edit pencils are hidden. Turn this on to edit text directly on the public site.'}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={editMode}
              onClick={() => setEditMode(!editMode)}
              data-testid="admin-edit-mode-toggle"
              className={`relative inline-flex h-7 w-14 shrink-0 cursor-pointer rounded-full border-2 transition-colors duration-200 ease-in-out focus:outline-none ${
                editMode
                  ? 'bg-primary/80 border-primary/80'
                  : 'bg-primary/10 border-primary/30'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-card shadow ring-0 transition duration-200 ease-in-out ${
                  editMode ? 'translate-x-7' : 'translate-x-1'
                } self-center`}
              />
              <span className="sr-only">Toggle edit mode</span>
            </button>
            <span
              className={`text-xs font-heading uppercase tracking-[0.25em] ${
                editMode ? 'text-primary' : 'text-primary/40'
              }`}
              data-testid="admin-edit-mode-status"
            >
              {editMode ? 'ON' : 'OFF'}
            </span>
          </div>

          {/* Tabs */}
          <div className="card-mystical rounded-2xl overflow-hidden mb-6">
            <div className="flex overflow-x-auto">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  data-testid={`admin-tab-${id}`}
                  className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 text-sm font-heading uppercase tracking-[0.25em] transition ${
                    tab === id
                      ? 'text-primary border-b-2 border-primary bg-primary/5'
                      : 'text-primary/50 border-b-2 border-transparent hover:text-primary/80'
                  }`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Overview */}
          {tab === 'overview' && stats && (
            <div className="space-y-6">
              {rzpStatus && (
                <div
                  data-testid="admin-razorpay-badge"
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-heading tracking-[0.25em] uppercase ${
                    rzpStatus.mode === 'live' && rzpStatus.auth_ok
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                      : rzpStatus.mode === 'test' && rzpStatus.auth_ok
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                      : 'border-rose-500/40 bg-rose-500/10 text-rose-300'
                  }`}
                  title={rzpStatus.error || rzpStatus.key_id_masked || ''}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      rzpStatus.auth_ok ? 'bg-current' : 'bg-rose-400'
                    }`}
                  />
                  Razorpay:&nbsp;
                  <strong className="tracking-[0.3em]">
                    {rzpStatus.mode === 'live'
                      ? 'LIVE'
                      : rzpStatus.mode === 'test'
                      ? 'TEST'
                      : 'UNKNOWN'}
                  </strong>
                  {!rzpStatus.auth_ok && (
                    <span className="opacity-80">· auth failed</span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard testid="stat-total" label="Total enrollments" value={stats.totals.enrollments} />
                <StatCard testid="stat-paid" label="Paid" value={stats.totals.paid} />
                <StatCard testid="stat-pending" label="Pending review" value={stats.totals.pending_review} />
                <StatCard testid="stat-users" label="Registered users" value={stats.totals.users} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard testid="stat-revenue" label="Revenue (INR)" value={fmtINR(stats.revenue_inr)} />
                <div className="card-mystical rounded-2xl p-5 md:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.35em] text-primary/60 mb-3">
                    Revenue by method
                  </div>
                  <div className="space-y-2">
                    {Object.entries(stats.revenue_by_method_inr || {}).map(([m, v]) => {
                      const total = stats.revenue_inr || 1;
                      const pct = Math.round((v / total) * 100);
                      const Meta = METHOD_META[m] || METHOD_META.other;
                      const Icon = Meta.icon;
                      return (
                        <div key={m} className="flex items-center gap-3 text-sm">
                          <span className={`flex items-center gap-2 w-28 ${Meta.color}`}>
                            <Icon size={14} /> {Meta.label}
                          </span>
                          <div className="flex-1 h-2 rounded-full bg-primary/10 overflow-hidden">
                            <div
                              className="h-full bg-primary/70"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="w-24 text-right text-primary/80 tabular-nums">
                            {fmtINR(v)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Enrollments */}
          {tab === 'enrollments' && (
            <div className="space-y-4">
              <div className="card-mystical rounded-2xl p-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-primary/70">
                  <Search size={14} />
                  <input
                    type="text"
                    value={filters.q}
                    onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                    placeholder="Search email / name / txn"
                    className="bg-primary/5 border border-primary/20 rounded-md px-3 py-1.5 text-sm text-primary focus:outline-none focus:border-primary/60"
                    data-testid="admin-search-input"
                  />
                </div>
                <select
                  value={filters.method}
                  onChange={(e) => setFilters((f) => ({ ...f, method: e.target.value }))}
                  className="bg-primary/5 border border-primary/20 rounded-md px-3 py-1.5 text-sm text-primary"
                  data-testid="admin-filter-method"
                >
                  <option value="all">All methods</option>
                  <option value="razorpay">Razorpay</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank</option>
                  <option value="other">Other</option>
                </select>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="bg-primary/5 border border-primary/20 rounded-md px-3 py-1.5 text-sm text-primary"
                  data-testid="admin-filter-status"
                >
                  <option value="all">All statuses</option>
                  <option value="paid">Paid</option>
                  <option value="pending_review">Pending review</option>
                  <option value="rejected">Rejected</option>
                  <option value="refunded">Refunded</option>
                </select>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-primary/60">
                    {enrollments.length} result{enrollments.length === 1 ? '' : 's'}
                  </span>
                  <Button
                    onClick={() => setShowAddModal(true)}
                    className="ornate-button px-4 py-2 text-sm"
                    data-testid="admin-add-enrollment"
                  >
                    <Plus size={14} /> Add enrollment
                  </Button>
                  <Button
                    onClick={downloadCsv}
                    className="ornate-button px-4 py-2 text-sm"
                    data-testid="admin-export-csv"
                  >
                    <Download size={14} /> Export CSV
                  </Button>
                </div>
              </div>

              <div className="card-mystical rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-enrollments-table">
                    <thead className="bg-primary/5 text-primary/70 uppercase text-[10px] tracking-[0.25em]">
                      <tr>
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">User</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Method</th>
                        <th className="text-left p-3">Txn ref</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.length === 0 && (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-primary/50">
                            {loading ? 'Loading…' : 'No enrollments match these filters'}
                          </td>
                        </tr>
                      )}
                      {enrollments.map((r) => {
                        const Meta = METHOD_META[r.method] || METHOD_META.other;
                        const Icon = Meta.icon;
                        const status = r.status || r.payment_status;
                        const S = STATUS_META[status] || STATUS_META.pending_review;
                        const enrollType = r.enrollment_type ||
                          (r.batch === 'live' ? 'Live Class' : 'Self-Paced');
                        const isLive = enrollType === 'Live Class';
                        return (
                          <tr
                            key={r.id}
                            className="border-t border-primary/10 hover:bg-primary/5"
                            data-testid={`enrollment-row-${r.id}`}
                          >
                            <td className="p-3 text-primary/70 tabular-nums whitespace-nowrap">
                              {fmtDate(r.created_at)}
                            </td>
                            <td className="p-3">
                              <div className="text-primary">{r.user_name || '—'}</div>
                              <div className="text-xs text-primary/60">{r.user_email || '—'}</div>
                              {(r.user_phone || r.user_country) && (
                                <div className="text-[11px] text-primary/45 mt-0.5">
                                  {r.user_country ? `${r.user_country} · ` : ''}
                                  {r.user_phone || ''}
                                </div>
                              )}
                            </td>
                            <td className="p-3">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider ${
                                  isLive
                                    ? 'bg-amber-500/15 text-amber-300 border border-amber-400/40'
                                    : 'bg-primary/10 text-primary/80 border border-primary/30'
                                }`}
                                data-testid={`enrollment-type-${r.id}`}
                              >
                                {enrollType}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1.5 ${Meta.color}`}>
                                <Icon size={12} /> {Meta.label}
                              </span>
                            </td>
                            <td className="p-3">
                              <code className="font-mono text-xs text-primary/80">
                                {r.txn_ref || r.payment_id || r.order_id || '—'}
                              </code>
                              {r.screenshot_url && (
                                <a
                                  href={r.screenshot_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ml-2 text-xs text-primary underline"
                                >
                                  screenshot
                                </a>
                              )}
                            </td>
                            <td className="p-3">
                              <span className={`inline-block rounded-full px-2 py-0.5 text-xs border ${S.color}`}>
                                {S.label}
                              </span>
                            </td>
                            <td className="p-3 text-right tabular-nums">
                              {fmtINR(r.amount_inr)}
                            </td>
                            <td className="p-3 text-right">
                              <div className="inline-flex items-center gap-1">
                                {status !== 'paid' && (
                                  <button
                                    type="button"
                                    title="Approve (mark paid)"
                                    onClick={() => updateEnrollment(r.id, 'paid')}
                                    className="p-1.5 rounded hover:bg-emerald-500/10 text-emerald-300"
                                    data-testid={`approve-${r.id}`}
                                  >
                                    <CheckCircle2 size={14} />
                                  </button>
                                )}
                                {status !== 'rejected' && (
                                  <button
                                    type="button"
                                    title="Reject"
                                    onClick={() => updateEnrollment(r.id, 'rejected')}
                                    className="p-1.5 rounded hover:bg-rose-500/10 text-rose-300"
                                    data-testid={`reject-${r.id}`}
                                  >
                                    <XCircle size={14} />
                                  </button>
                                )}
                                {status === 'paid' && (
                                  <button
                                    type="button"
                                    title="Mark refunded"
                                    onClick={() => updateEnrollment(r.id, 'refunded')}
                                    className="p-1.5 rounded hover:bg-slate-500/10 text-slate-300"
                                    data-testid={`refund-${r.id}`}
                                  >
                                    <Undo2 size={14} />
                                  </button>
                                )}
                                {status === 'rejected' && (
                                  <button
                                    type="button"
                                    title="Mark pending"
                                    onClick={() => updateEnrollment(r.id, 'pending_review')}
                                    className="p-1.5 rounded hover:bg-amber-500/10 text-amber-300"
                                    data-testid={`pending-${r.id}`}
                                  >
                                    <Clock size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-xs text-primary/50">
                Method counts (current filter): Razorpay {methodCounts.razorpay} · UPI{' '}
                {methodCounts.upi} · Bank {methodCounts.bank} · Other {methodCounts.other}
              </div>
            </div>
          )}

          {/* Orders */}
          {tab === 'orders' && (
            <div className="card-mystical rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-orders-table">
                  <thead className="bg-primary/5 text-primary/70 uppercase text-[10px] tracking-[0.25em]">
                    <tr>
                      <th className="text-left p-3">Created</th>
                      <th className="text-left p-3">Order ID</th>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Amount</th>
                      <th className="text-left p-3">Payment ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-primary/50">
                          {loading ? 'Loading…' : 'No orders yet'}
                        </td>
                      </tr>
                    )}
                    {orders.map((o) => (
                      <tr
                        key={o.order_id}
                        className="border-t border-primary/10 hover:bg-primary/5"
                        data-testid={`order-row-${o.order_id}`}
                      >
                        <td className="p-3 text-primary/70 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                        <td className="p-3 font-mono text-xs text-primary/90">{o.order_id}</td>
                        <td className="p-3 text-primary/80">{o.user_id?.slice(0, 8)}…</td>
                        <td className="p-3">
                          <span className="inline-block rounded-full px-2 py-0.5 text-xs border border-primary/30 text-primary/80">
                            {o.status}
                          </span>
                        </td>
                        <td className="p-3 text-right tabular-nums">
                          {fmtINR((o.amount || 0) / 100)}
                        </td>
                        <td className="p-3 font-mono text-xs text-primary/70">
                          {o.payment_id || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Users */}
          {tab === 'users' && (
            <div className="card-mystical rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead className="bg-primary/5 text-primary/70 uppercase text-[10px] tracking-[0.25em]">
                    <tr>
                      <th className="text-left p-3">Joined</th>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Level</th>
                      <th className="text-left p-3">Enrolled</th>
                      <th className="text-left p-3">Completed</th>
                      <th className="text-left p-3">Membership</th>
                      <th className="text-right p-3">Streak</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={9} className="p-8 text-center text-primary/50">
                          {loading ? 'Loading…' : 'No users'}
                        </td>
                      </tr>
                    )}
                    {users.map((u) => (
                      <tr
                        key={u.id}
                        className="border-t border-primary/10 hover:bg-primary/5"
                      >
                        <td className="p-3 text-primary/70 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                        <td className="p-3 text-primary">{u.name}</td>
                        <td className="p-3 text-primary/80">{u.email}</td>
                        <td className="p-3 tabular-nums">{u.current_level}</td>
                        <td className="p-3 text-primary/80">
                          {(u.enrolled_levels || []).join(', ') || '—'}
                        </td>
                        <td className="p-3 text-primary/80">
                          {(u.completed_levels || []).join(', ') || '—'}
                        </td>
                        <td className="p-3 text-primary/80" data-testid={`membership-cell-${u.id}`}>
                          <MembershipBadge m={u.membership} />
                        </td>
                        <td className="p-3 text-right tabular-nums">{u.streak_days || 0}</td>
                        <td className="p-3 text-right">
                          <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                            <button
                              type="button"
                              onClick={() => openAccessEditor(u)}
                              className="px-2 py-1 text-[11px] font-medium uppercase tracking-[0.2em] border border-primary/30 rounded-lg text-primary/90 hover:bg-primary/10 transition"
                              data-testid={`edit-access-btn-${u.id}`}
                            >
                              Edit access
                            </button>
                            <MembershipAdminActions
                              user={u}
                              onDone={refresh}
                            />
                            <button
                              type="button"
                              onClick={() => handleResetPassword(u)}
                              disabled={resettingId === u.id}
                              className="px-2 py-1 text-[11px] font-medium uppercase tracking-[0.2em] border border-amber-300/30 rounded-lg text-amber-200/90 hover:bg-amber-400/10 transition disabled:opacity-50"
                              data-testid={`reset-password-btn-${u.id}`}
                            >
                              {resettingId === u.id ? 'Sending…' : 'Reset password'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Blog Writers */}
          {tab === 'writers' && (
            <div className="card-mystical rounded-2xl overflow-hidden" data-testid="admin-writers-panel">
              <div className="flex items-center justify-between gap-3 p-4 border-b border-primary/15 flex-wrap">
                <div className="text-sm text-primary/80">
                  <span className="text-primary font-medium">{writers.total_writers}</span>
                  {' '}of {writers.max_writers} Writer slots used
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-primary/40" />
                  <input
                    type="text"
                    value={writerSearch}
                    onChange={(e) => setWriterSearch(e.target.value)}
                    placeholder="Search by name or email"
                    className="bg-primary/5 border border-primary/20 rounded-lg pl-9 pr-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60 w-72 max-w-full"
                    data-testid="writers-search-input"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-primary/5 text-primary/70 uppercase text-[10px] tracking-[0.25em]">
                    <tr>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Role</th>
                      <th className="text-right p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWriterRows.length === 0 && (
                      <tr><td colSpan={4} className="p-8 text-center text-primary/50">No users match</td></tr>
                    )}
                    {filteredWriterRows.map((u) => {
                      const isWriter = u.role === 'writer';
                      const disabled = !isWriter && atWriterCap; // granting is blocked when capped
                      return (
                        <tr key={u.id} className="border-t border-primary/10 hover:bg-primary/5" data-testid={`writer-row-${u.id}`}>
                          <td className="p-3 text-primary">{u.name}</td>
                          <td className="p-3 text-primary/80">{u.email}</td>
                          <td className="p-3">
                            <span className={`inline-flex px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] border ${
                              isWriter
                                ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30'
                                : 'bg-slate-500/15 text-slate-300 border-slate-400/30'
                            }`}>
                              {isWriter ? 'Writer' : 'Reader'}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <button
                              type="button"
                              onClick={() => toggleWriter(u)}
                              disabled={disabled || togglingWriterId === u.id}
                              title={disabled ? `Maximum ${writers.max_writers} writers reached. Remove a writer before adding another.` : ''}
                              className={`px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] border rounded-lg transition ${
                                disabled
                                  ? 'opacity-40 cursor-not-allowed border-primary/20 text-primary/60'
                                  : 'border-primary/30 text-primary/90 hover:bg-primary/10'
                              }`}
                              data-testid={`toggle-writer-${u.id}`}
                            >
                              {togglingWriterId === u.id ? '…' : (isWriter ? 'Revoke writer' : 'Make writer')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'emails' && (
            <EmailsTab token={token} user={user} />
          )}
        </motion.div>
      </div>

      {/* Add enrollment modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => !adding && setShowAddModal(false)}
          data-testid="add-enrollment-modal"
        >
          <div
            className="card-mystical rounded-2xl p-6 max-w-xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading text-primary uppercase tracking-wider">
                Record manual enrollment
              </h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-primary/60 hover:text-primary"
                data-testid="close-add-enrollment"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submitManualEnrollment} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { k: 'user_name', label: 'Name *', placeholder: 'Full name' },
                  { k: 'user_email', label: 'Email *', type: 'email', placeholder: 'user@example.com' },
                  { k: 'user_country', label: 'Country', placeholder: 'e.g. Poland' },
                  { k: 'user_phone', label: 'Phone', placeholder: '+XX…' },
                  { k: 'txn_ref', label: 'Transaction ref', placeholder: 'UTR / Remitly ref' },
                  { k: 'amount_inr', label: 'Amount (₹)', type: 'number' },
                ].map((f) => (
                  <label key={f.k} className="block">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60 block mb-1">
                      {f.label}
                    </span>
                    <input
                      type={f.type || 'text'}
                      value={manualForm[f.k]}
                      onChange={(e) =>
                        setManualForm((prev) => ({ ...prev, [f.k]: e.target.value }))
                      }
                      placeholder={f.placeholder || ''}
                      className="w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60"
                      data-testid={`manual-${f.k}`}
                    />
                  </label>
                ))}
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60 block mb-1">
                  Method
                </span>
                <select
                  value={manualForm.method}
                  onChange={(e) => setManualForm((p) => ({ ...p, method: e.target.value }))}
                  className="w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 text-sm text-primary"
                  data-testid="manual-method"
                >
                  <option value="upi">UPI</option>
                  <option value="bank">Bank transfer</option>
                  <option value="other">Other (Remitly / Wise / international)</option>
                  <option value="razorpay">Razorpay</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60 block mb-1">
                  Notes
                </span>
                <textarea
                  value={manualForm.notes}
                  onChange={(e) => setManualForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  className="w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60"
                  data-testid="manual-notes"
                />
              </label>
              <p className="text-xs text-primary/50 leading-relaxed">
                Marked as <strong>paid</strong> immediately. If this email already has an account
                the user gets auto-enrolled in Grahita Shakti. Otherwise the record auto-links when they
                register.
              </p>
              <Button
                type="submit"
                disabled={adding}
                className="ornate-button w-full"
                data-testid="submit-manual-enrollment"
              >
                {adding ? 'Saving…' : 'Record enrollment'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Edit access modal */}
      {accessEditUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => !savingAccess && setAccessEditUser(null)}
          data-testid="edit-access-modal"
        >
          <div
            className="card-mystical rounded-2xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setAccessEditUser(null)}
              className="absolute top-4 right-4 text-primary/60 hover:text-primary"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <OrnateDivider variant="lotus" className="mb-4 max-w-xs mx-auto" />
            <h3 className="text-xl font-heading golden-text text-center mb-1">
              Edit level access
            </h3>
            <p className="text-center text-xs text-primary/60 mb-5 break-all">
              {accessEditUser.email}
            </p>
            <div className="space-y-2 mb-6">
              {[1, 2, 3, 4].map((n) => (
                <label
                  key={n}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg border border-primary/20 hover:bg-primary/5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={accessLevels.includes(n)}
                    onChange={() => toggleAccessLevel(n)}
                    className="accent-primary w-4 h-4"
                    data-testid={`access-level-${n}-checkbox`}
                  />
                  <span className="text-sm text-primary/90">
                    {n === 1 ? 'Grahita Shakti' : `Level ${n}`}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAccessEditUser(null)}
                disabled={savingAccess}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={saveUserAccess}
                disabled={savingAccess}
                className="ornate-button flex-1"
                data-testid="save-access-btn"
              >
                {savingAccess ? 'Saving…' : 'Save access'}
              </Button>
            </div>
            <p className="text-[11px] text-primary/50 text-center mt-4 leading-relaxed">
              Overrides <code>enrolled_levels</code> directly. Does not create payment records.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;

/* ====================================================================
   EmailsTab — Phase-1 admin panel for emails.
   • Per-type subscriber counts + sent/failed counts
   • Last-30-days log table
   • "Send test" button for every email type
   ==================================================================== */
const EmailsTab = ({ token, user: _user }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [log, setLog] = useState([]);
  const [sendingType, setSendingType] = useState('');
  const [filter, setFilter] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/emails/stats`, auth),
        axios.get(`${API}/admin/emails/log`, auth),
      ]);
      setStats(s.data);
      setLog(l.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load emails data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSendTest = async (emailType) => {
    setSendingType(emailType);
    try {
      await axios.post(`${API}/admin/emails/test`,
        { email_type: emailType },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Test sent · ${emailType}`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setSendingType('');
    }
  };

  const handleManualScan = async (kind) => {
    const labels = { reengagement: 'Re-engagement scan', 'blog-publish': 'Blog publish broadcast' };
    setSendingType(`scan-${kind}`);
    try {
      const res = await axios.post(`${API}/admin/emails/scan/${kind}`,
        {}, { headers: { Authorization: `Bearer ${token}` } });
      const detail = kind === 'reengagement'
        ? `R1=${res.data.r1 ?? 0} · R2=${res.data.r2 ?? 0}`
        : `${res.data.broadcasts ?? 0} broadcast(s)`;
      toast.success(`${labels[kind]} done · ${detail}`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally {
      setSendingType('');
    }
  };

  const countFor = (type) =>
    stats?.type_counts?.find((c) => c.email_type === type) || { sent: 0, failed: 0, total: 0 };

  const filteredLog = filter
    ? log.filter((r) => (r.email_type || '').includes(filter)
        || (r.recipient || '').toLowerCase().includes(filter.toLowerCase())
        || (r.subject || '').toLowerCase().includes(filter.toLowerCase()))
    : log;

  if (loading) {
    return (
      <div className="card-mystical rounded-2xl p-10 text-center text-primary/70">
        <Loader2 className="animate-spin inline-block mr-2" size={18} /> Loading email data…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-emails-tab">
      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-mystical rounded-xl p-4">
          <div className="text-[10px] tracking-[0.3em] text-primary/60 uppercase mb-1">Total users</div>
          <div className="text-2xl font-heading golden-text">{stats?.total_users ?? 0}</div>
        </div>
        <div className="card-mystical rounded-xl p-4">
          <div className="text-[10px] tracking-[0.3em] text-primary/60 uppercase mb-1">Blog subscribed</div>
          <div className="text-2xl font-heading golden-text">{stats?.blog_subscribed ?? 0}</div>
          <div className="text-[11px] text-primary/55 mt-0.5">{stats?.blog_unsubscribed ?? 0} unsubscribed</div>
        </div>
        <div className="card-mystical rounded-xl p-4">
          <div className="text-[10px] tracking-[0.3em] text-primary/60 uppercase mb-1">Re-engagement subs</div>
          <div className="text-2xl font-heading golden-text">{stats?.reengagement_subscribed ?? 0}</div>
          <div className="text-[11px] text-primary/55 mt-0.5">{stats?.reengagement_unsubscribed ?? 0} opted out</div>
        </div>
        <div className="card-mystical rounded-xl p-4">
          <div className="text-[10px] tracking-[0.3em] text-primary/60 uppercase mb-1">Sent (last 30d)</div>
          <div className="text-2xl font-heading golden-text">{log.filter((r) => r.status === 'sent').length}</div>
          <div className="text-[11px] text-primary/55 mt-0.5">{log.filter((r) => r.status === 'failed').length} failed</div>
        </div>
      </div>

      {/* Email type grid with test-send buttons */}
      <div className="card-mystical rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-primary/15 flex items-center gap-2 flex-wrap justify-between">
          <div className="flex items-center gap-2">
            <Mail size={14} className="text-primary/70" />
            <span className="text-sm font-heading uppercase tracking-[0.25em] text-primary/80">Email types</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => handleManualScan('reengagement')}
              disabled={sendingType === 'scan-reengagement'}
              data-testid="emails-scan-reengagement"
            >
              {sendingType === 'scan-reengagement' ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
              Run re-engagement scan
            </Button>
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => handleManualScan('blog-publish')}
              disabled={sendingType === 'scan-blog-publish'}
              data-testid="emails-scan-blog-publish"
            >
              {sendingType === 'scan-blog-publish' ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
              Run blog publish scan
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-primary/10">
          {(stats?.email_types || []).map((t) => {
            const c = countFor(t);
            return (
              <div
                key={t}
                className="flex items-center justify-between gap-3 px-4 py-3 bg-background"
                data-testid={`email-type-row-${t}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-primary/90 font-medium truncate">{t}</div>
                  <div className="text-[11px] text-primary/55 mt-0.5">
                    {c.sent} sent · {c.failed} failed
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleSendTest(t)}
                  disabled={sendingType === t}
                  className="shrink-0"
                  data-testid={`send-test-${t}`}
                >
                  {sendingType === t
                    ? <Loader2 className="animate-spin" size={12} />
                    : <Send size={12} />}
                  Send test
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Log table */}
      <div className="card-mystical rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-primary/15 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Mail size={14} className="text-primary/70" />
            <span className="text-sm font-heading uppercase tracking-[0.25em] text-primary/80">
              Recent emails (last 30 days)
            </span>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-primary/40" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by type, email, subject"
              className="bg-primary/5 border border-primary/20 rounded-lg pl-9 pr-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60 w-72 max-w-full"
              data-testid="emails-log-filter"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="emails-log-table">
            <thead className="bg-primary/5 text-[11px] tracking-[0.2em] uppercase text-primary/65">
              <tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Subject</th>
                <th className="text-left px-4 py-2.5">Recipient</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary/10">
              {filteredLog.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-primary/55">No emails to show</td></tr>
              )}
              {filteredLog.map((row) => (
                <tr key={row.id} className="hover:bg-primary/5">
                  <td className="px-4 py-2.5 text-[12px] text-primary/70 whitespace-nowrap">{fmtDate(row.sent_at)}</td>
                  <td className="px-4 py-2.5 text-[12px] text-primary/85">{row.email_type}</td>
                  <td className="px-4 py-2.5 text-[12px] text-primary/85 max-w-md truncate" title={row.subject}>{row.subject}</td>
                  <td className="px-4 py-2.5 text-[12px] text-primary/70">{row.recipient_name || ''} <span className="text-primary/45">·</span> {row.recipient}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.2em] ${
                        row.status === 'sent' ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                        : row.status === 'failed' ? 'bg-red-500/10 text-red-300 border border-red-500/30'
                        : 'bg-primary/10 text-primary/65 border border-primary/25'
                      }`}
                      title={row.error || ''}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
